import { EVENT_PRICE_CARD_FRAGMENT } from '@/gql/fragments/entryCards.gql';
import { createFragmentArrayParser } from '@liquorice/gql-utils';
import { toString } from '@liquorice/utils';
import { EventPriceCardFragment } from '@/__generated__/graphql';

type EventPrice = {
  title?: string;
  price?: string;
};

/**
 * Formats a price string by removing a leading 'A' (if immediately followed by a '$')
 * and stripping any trailing '.00' from the value.
 *
 * @param value - The price string to format (e.g., "A$20.00", "$15.50", "A$10").
 * @returns The formatted price string without the leading 'A' and trailing '.00', or `undefined` if input is `undefined`.
 */
const formatPrice = (value?: string) =>
  value?.replace(/^A(?=\$)/, '').replace(/\.00$/, '') ?? undefined;

export const parseEventPrice = (data: Maybe<EventPriceCardFragment>): EventPrice | null => {
  if (!data) return null;

  const { title, maxPrice, minPrice } = data;

  const minPriceFormatted = formatPrice(minPrice);
  const maxPriceFormatted = formatPrice(maxPrice);

  const min = minPriceFormatted ?? '';
  const max = maxPriceFormatted ?? '';

  const price = `${min}${min && max ? ' - ' : ''}${max}`;

  return {
    title: toString(title),
    price,
  };
};

export const parseEventPriceFragment = createFragmentArrayParser(
  EVENT_PRICE_CARD_FRAGMENT,
  (items) => {
    return items.map((item) => parseEventPrice(item));
  }
);
